<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      {{
        $router.currentRoute.value.name == "UpdatePodcaster" ? "Update" : "Add"
      }}
      Podcaster
    </h2>
    <form @submit.prevent="submitForm">
      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Podcaster Information</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="name">Name </label>
              <InputText
                id="name"
                v-model="form.name"
                :class="{ 'p-invalid': submitted && !form.name }"
              ></InputText>
              <small v-if="submitted && !form.name" class="p-error"
                >Name is required</small
              >
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="email">Email </label>
              <InputText
                id="email"
                type="email"
                v-model="form.email"
                :class="{
                  'p-invalid': submitted && (!form.email || !emailValid),
                }"
              ></InputText>
              <small v-if="submitted && !emailValid" class="p-error"
                >Invalid email format</small
              >
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="password">Password </label>
              <InputText
                id="password"
                type="password"
                autocomplete="new-password"
                placeholder="Enter password"
                v-model="form.password"
                :class="{
                  'p-invalid': submitted && (!form.password || !passwordValid),
                }"
              ></InputText>
              <small
                v-if="submitted && !form.podcasterId && !passwordValid"
                class="p-error"
                >Password must be at least 8 chars, with upper-case and
                lower-case letters</small
              >
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="status">Status</label>
              <Dropdown
                id="status"
                v-model="form.status"
                :options="statusOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Choose Status"
              />
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="type">Type</label>
              <Dropdown
                id="podcaster_type"
                v-model="form.podcaster_type"
                :options="typeOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Choose Type"
              />
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="plan">Plan</label>
              <Dropdown
                id="plan"
                v-model="form.plan"
                :options="podcasterPlanOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Choose Type"
              />
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="revshare">Revenue Share</label>
              <InputNumber
                id="revshare"
                v-model="form.revshare"
                placeholder="0.00"
                :minFractionDigits="2"
                :min="0"
                :max="1"
                :step="0.01"
              />
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="override_network_revshare"
                >Override Network Revshare</label
              >
              <InputNumber
                id="override_network_revshare"
                v-model="form.override_network_revshare"
                placeholder="0.00"
                :minFractionDigits="2"
                :min="0"
                :max="1"
                :step="0.01"
              />
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="connected_publisher_id">Connected Publisher ID</label>
              <Dropdown
                input-id="connected_publisher_id"
                v-model="form.connected_publisher_id"
                :options="groups"
                optionLabel="label"
                optionValue="id"
                placeholder="Select Publisher"
                show-clear
                filter
                filter-placeholder="Search by name"
                :filter-fields="['label']"
              />
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="network_id">Network</label>
              <Dropdown
                input-id="network_id"
                v-model="form.network_id"
                :options="networks"
                optionLabel="name"
                optionValue="network_id"
                placeholder="Select Network"
                show-clear
                filter
                filter-placeholder="Search by name"
                :filter-fields="['name']"
              />
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="payment_terms">Payment Terms (days)</label>
              <InputText
                id="payment_terms"
                v-model="form.payment_terms"
                placeholder="e.g. 2 or 30"
                :class="{
                  'p-invalid':
                    (submitted && !!form.payment_terms && !paymentTermsValid) ||
                    form.payment_terms === '0',
                }"
              />
              <small
                v-if="
                  submitted &&
                  ((!!form.payment_terms && !paymentTermsValid) ||
                    form.payment_terms === '0')
                "
                class="p-error"
                >Payment terms must be a positive number of days.</small
              >
            </div>
          </div>
        </template>
      </Card>
      <div class="w-full flex justify-content-end gap-2">
        <Button
          class="flex-1 sm:flex-initial"
          outlined
          label="Cancel"
          @click="router.go(-1)"
        />
        <Button class="flex-1 sm:flex-initial" type="submit" label="Submit" />
      </div>
    </form>
  </div>
</template>

<script setup>
import Card from "primevue/card";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import Checkbox from "primevue/checkbox";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";
import axios from "axios";
import { API_BASE_URL, podcasterPlanOptions } from "@/consts";
import { onMounted } from "vue";
import { computed } from "vue";
import { emailValidator, passwordValidator } from "@/helpers";
import InputNumber from "primevue/inputnumber";

const router = useRouter();
const store = useStore();
const toast = useToast();

const submitted = ref(false);
const networks = ref([]);

const statusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Form Submitted", value: "ob_form_submitted" },
  { label: "Active", value: "active" },
];
const typeOptions = [
  { label: "Individual", value: "individual" },
  { label: "Network", value: "network" },
  { label: "Publisher", value: "publisher" },
];

const form = ref({
  name: "",
  email: "",
  password: "",
  status: statusOptions[0].value,
  podcaster_type: typeOptions[0].value,
  plan: podcasterPlanOptions[0].value,
  connected_publisher_id: null,
  override_network_revshare: 0,
  revshare: 0,
  network_id: null,
  payment_terms: "",
});

const podcasterId = router.currentRoute.value.params.podcasterId;
const token = localStorage.getItem("podcastAdminToken");

const emailValid = computed(() => emailValidator(form.value.email));
const passwordValid = computed(() => passwordValidator(form.value.password));
const paymentTermsValid = computed(() => {
  if (!form.value.payment_terms) return true;
  return (
    /^[0-9]+$/.test(String(form.value.payment_terms)) &&
    Number(form.value.payment_terms) > 0
  );
});

const groups = computed(() => store.getters.groups || []);

const fetchGroups = () => {
  if (!store.getters.groups || store.getters.groups.length == 0) {
    store.dispatch("loadGroups", { token });
  }
};

const fetchNetworks = async () => {
  try {
    store.commit("setLoading", true);
    const response = await axios.get(API_BASE_URL + "/podcast-networks", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data?.success) {
      networks.value = response.data.data || [];
    }
  } catch (error) {
    console.error("Error fetching networks:", error);
  } finally {
    store.commit("setLoading", false);
  }
};

const getPodcasterData = async () => {
  try {
    const response = await axios.get(
      API_BASE_URL + "/podcaster/detail/" + podcasterId,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data.success) {
      const {
        name,
        email,
        status,
        podcaster_type,
        plan,
        avg_per_episode,
        avg_per_month,
        revshare,
        connected_publisher_id,
        override_network_revshare,
        network_id,
      } = response.data.podcaster;
      form.value = {
        ...form.value,
        name,
        email,
        status: status?.length ? status : statusOptions[0].value,
        podcaster_type: podcaster_type?.length
          ? podcaster_type
          : typeOptions[0].value,
        plan: plan?.length ? plan : typeOptions[0].value,
        revshare,
        connected_publisher_id: parseInt(connected_publisher_id),
        override_network_revshare: override_network_revshare || 0,
        network_id: network_id || null,
        payment_terms: response.data.podcaster.payment_terms || "",
      };
    } else
      toast.add({
        severity: "error",
        summary: "Podcaster data not found",
        life: 4000,
      });
  } catch (error) {
    console.error(error);
    toast.add({
      severity: "error",
      summary: "Something went wrong...",
      life: 4000,
    });
  }
};

const submitForm = async () => {
  submitted.value = true;
  if (
    form.value.name &&
    form.value.email &&
    emailValid.value &&
    (form.value.podcasterId || passwordValid.value) &&
    paymentTermsValid.value
  ) {
    try {
      store.commit("setLoading", true);

      const response = await axios.post(
        API_BASE_URL + "/add-podcaster",
        form.value,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        // Clear form after successful submission
        form.value = {
          name: "",
          email: "",
          password: "",
          status: statusOptions[0].value,
          podcaster_type: typeOptions[0].value,
          plan: podcasterPlanOptions[0].value,
          connected_publisher_id: null,
          override_network_revshare: 0,
          network_id: null,
          payment_terms: "",
        };

        submitted.value = false;
        store.dispatch("loadPodcasters", { token });
        toast.add({
          severity: "success",
          summary: "Success",
          detail: `Podcaster ${
            podcasterId ? "updated" : "added"
          } successfully!`,
          life: 4000,
        });
        router.replace({ name: "PodcastersList" });
      } else {
        toast.add({
          severity: "error",
          detail: `Something went wrong...`,
          life: 4000,
        });
      }
    } catch (error) {
      toast.add({
        severity: "error",
        detail: `Something went wrong...`,
        life: 4000,
      });
      console.log(error);
    } finally {
      store.commit("setLoading", false);
    }
  }
};

onMounted(async () => {
  store.commit("setLoading", true);
  fetchGroups();
  await fetchNetworks();
  if (podcasterId) {
    form.value.podcasterId = podcasterId;
    await getPodcasterData();
  }
  store.commit("setLoading", false);
});
</script>
